'use client';

import { Card, Text } from '@terra/ui-design-system';
import { Box, Grid, GridItem, HStack, VStack } from '@terra/ui-styled-system';
import {
  Alert01Icon,
  BookOpen01Icon,
  CheckmarkCircle02Icon,
  File01Icon,
  SquareLock01Icon,
  ScrollIcon,
  CancelCircleIcon,
  FlashIcon,
} from '@terra/ui-icons';
import type { SkillsExplainerLabels } from '../types';

export function SkillsIntroBlock({
  labels,
}: {
  labels: SkillsExplainerLabels;
}) {
  return (
    <HStack gap={3} alignItems="flex-start" w="full">
      <VStack alignItems="stretch" gap={1.5} flex="1" minW="0">
        {labels.eyebrow ? (
          <Text
            fontSize="2xs"
            color="fg.muted"
            textTransform="uppercase"
            letterSpacing="wider"
            fontWeight="semibold"
          >
            {labels.eyebrow}
          </Text>
        ) : null}
        <Text
          fontSize="lg"
          fontWeight="semibold"
          color="fg.default"
          lineHeight="1.3"
        >
          {labels.title}
        </Text>
        <Text fontSize="sm" color="fg.muted" lineHeight="1.65" maxW="780px">
          {labels.paragraph}
        </Text>
      </VStack>
    </HStack>
  );
}

export function SkillsExplainer({ labels }: { labels: SkillsExplainerLabels }) {
  return (
    <Card.Root
      w="full"
      mt={2}
      boxShadow="none"
      borderWidth="1px"
      borderStyle="solid"
      borderColor="border.neutral.muted"
      bg="bg.neutral.subtleSoft"
    >
      <Card.Body p={5}>
        <VStack alignItems="stretch" gap={6} w="full">
          <SkillsIntroBlock labels={labels} />

          <Box
            borderRadius="md"
            borderWidth="1px"
            borderStyle="solid"
            borderColor="border.neutral.muted"
            bg="bg.default"
            p={4}
          >
            <VStack alignItems="stretch" gap={3}>
              <HStack gap={2} alignItems="center">
                <Box color="fg.muted">
                  <BookOpen01Icon size={14} />
                </Box>
                <Text
                  fontSize="2xs"
                  fontWeight="semibold"
                  color="fg.default"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  {labels.anatomyTitle}
                </Text>
              </HStack>
              <Text fontSize="sm" color="fg.muted" lineHeight="1.65">
                {labels.anatomyDescription}
              </Text>
              <Grid columns={{ base: 1, md: 3 }} gap={3}>
                <AnatomyTile
                  icon={<ScrollIcon size={14} />}
                  title={labels.anatomyFrontmatterTitle}
                  body={labels.anatomyFrontmatterBody}
                />
                <AnatomyTile
                  icon={<File01Icon size={14} />}
                  title={labels.anatomyInstructionsTitle}
                  body={labels.anatomyInstructionsBody}
                />
                <AnatomyTile
                  icon={<FlashIcon size={14} />}
                  title={labels.anatomyTriggerTitle}
                  body={labels.anatomyTriggerBody}
                />
              </Grid>
            </VStack>
          </Box>

          <Box
            borderRadius="md"
            borderWidth="1px"
            borderStyle="solid"
            borderColor="border.neutral.muted"
            bg="bg.default"
            p={4}
          >
            <VStack alignItems="stretch" gap={3}>
              <Text
                fontSize="2xs"
                fontWeight="semibold"
                color="fg.default"
                textTransform="uppercase"
                letterSpacing="wider"
              >
                {labels.exampleTitle}
              </Text>
              <Box
                as="pre"
                borderRadius="sm"
                bg="bg.neutral.subtleSoft"
                borderWidth="1px"
                borderStyle="solid"
                borderColor="border.neutral.muted"
                fontSize="xs"
                fontFamily="mono"
                color="fg.default"
                px={3}
                py={2.5}
                whiteSpace="pre-wrap"
                lineHeight="1.7"
                overflow="auto"
              >
                {labels.exampleSnippet}
              </Box>
              <Text fontSize="xs" color="fg.muted" lineHeight="1.65">
                {labels.exampleCaption}
              </Text>
            </VStack>
          </Box>

          <ToolValidationCallout
            eyebrow={labels.toolValidationEyebrow}
            title={labels.toolValidationTitle}
            body={labels.toolValidationBody}
            bullets={labels.toolValidationBullets}
          />

          <Grid w="full" columns={{ base: 1, md: 2 }} gap={3}>
            <GridItem>
              <GuidanceCard
                tone="positive"
                icon={<CheckmarkCircle02Icon size={14} />}
                title={labels.whenToUseTitle}
                items={labels.whenToUseItems}
              />
            </GridItem>
            <GridItem>
              <GuidanceCard
                tone="negative"
                icon={<CancelCircleIcon size={14} />}
                title={labels.whenNotToUseTitle}
                items={labels.whenNotToUseItems}
              />
            </GridItem>
          </Grid>

          <HStack
            gap={3}
            alignItems="flex-start"
            borderRadius="md"
            borderWidth="1px"
            borderStyle="solid"
            borderColor="border.neutral.muted"
            bg="bg.default"
            p={3}
          >
            <Box flexShrink={0} color="fg.muted" mt={0.5}>
              <SquareLock01Icon size={14} />
            </Box>
            <VStack alignItems="flex-start" gap={0.5}>
              <Text fontSize="xs" fontWeight="semibold" color="fg.default">
                {labels.tenantScopeTitle}
              </Text>
              <Text fontSize="xs" color="fg.muted" lineHeight="1.65">
                {labels.tenantScopeBody}
              </Text>
            </VStack>
          </HStack>
        </VStack>
      </Card.Body>
    </Card.Root>
  );
}

function ToolValidationCallout({
  eyebrow,
  title,
  body,
  bullets,
}: {
  eyebrow: string;
  title: string;
  body: string;
  bullets: string[];
}) {
  return (
    <HStack
      gap={3}
      alignItems="flex-start"
      borderRadius="md"
      borderWidth="1px"
      borderStyle="solid"
      borderColor="orange.muted"
      bg="orange.subtle"
      p={4}
    >
      <Box
        flexShrink={0}
        p={2}
        borderRadius="md"
        bg="bg.default"
        borderWidth="1px"
        borderStyle="solid"
        borderColor="orange.muted"
        color="orange.fg"
      >
        <Alert01Icon size={16} />
      </Box>
      <VStack alignItems="stretch" gap={1.5} flex="1" minW="0">
        <Text
          fontSize="2xs"
          fontWeight="semibold"
          color="orange.fg"
          textTransform="uppercase"
          letterSpacing="wider"
        >
          {eyebrow}
        </Text>
        <Text fontSize="sm" fontWeight="semibold" color="fg.default">
          {title}
        </Text>
        <Text fontSize="sm" color="fg.muted" lineHeight="1.65">
          {body}
        </Text>
        {bullets.length > 0 ? (
          <VStack alignItems="stretch" gap={1} pt={1}>
            {bullets.map((item, idx) => (
              <HStack key={idx} alignItems="flex-start" gap={2}>
                <Box
                  mt={1.5}
                  w="3px"
                  h="3px"
                  borderRadius="full"
                  bg="orange.fg"
                  flexShrink={0}
                />
                <Text fontSize="xs" color="fg.default" lineHeight="1.65">
                  {item}
                </Text>
              </HStack>
            ))}
          </VStack>
        ) : null}
      </VStack>
    </HStack>
  );
}

function AnatomyTile({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <Box
      p={3}
      borderRadius="md"
      borderWidth="1px"
      borderStyle="solid"
      borderColor="border.neutral.muted"
      bg="bg.neutral.subtleSoft"
      h="full"
    >
      <VStack alignItems="flex-start" gap={1.5}>
        <HStack gap={1.5} alignItems="center">
          <Box color="fg.muted">{icon}</Box>
          <Text fontSize="xs" fontWeight="semibold" color="fg.default">
            {title}
          </Text>
        </HStack>
        <Text fontSize="xs" color="fg.muted" lineHeight="1.65">
          {body}
        </Text>
      </VStack>
    </Box>
  );
}

function GuidanceCard({
  tone,
  icon,
  title,
  items,
}: {
  tone: 'positive' | 'negative';
  icon: React.ReactNode;
  title: string;
  items: string[];
}) {
  const dotColor = tone === 'positive' ? 'fg.success' : 'fg.muted';
  const iconColor = tone === 'positive' ? 'fg.success' : 'fg.muted';

  return (
    <Box
      p={3}
      borderRadius="md"
      borderWidth="1px"
      borderStyle="solid"
      borderColor="border.neutral.muted"
      bg="bg.default"
      h="full"
    >
      <HStack gap={1.5} alignItems="center" mb={2}>
        <Box color={iconColor}>{icon}</Box>
        <Text fontSize="xs" fontWeight="semibold" color="fg.default">
          {title}
        </Text>
      </HStack>
      <VStack alignItems="stretch" gap={1.5}>
        {items.map((item, idx) => (
          <HStack key={idx} alignItems="flex-start" gap={2}>
            <Box
              mt={1.5}
              w="3px"
              h="3px"
              borderRadius="full"
              bg={dotColor}
              flexShrink={0}
            />
            <Text fontSize="sm" color="fg.muted" lineHeight="1.65">
              {item}
            </Text>
          </HStack>
        ))}
      </VStack>
    </Box>
  );
}
